<template>
  <FlexLayout role="region">
    <vue-loading :active="isLoading" :is-full-page="false" />

    <BlockContainer>
      <template v-if="isTopUp || isSchoolTopUp || isMealPlan">
        <p
          v-if="paymentFailed"
          ref="errorMsg"
          aria-live="assertive"
          tabindex="-1"
        >
          {{
            isTopUp || isSchoolTopUp
              ? $t('notification.chargeError')
              : $t('notification.paymentError')
          }}
        </p>
        <template v-else>
          <p>
            {{
              isTopUp || isSchoolTopUp
                ? $t('notification.chargeSuccessful')
                : $t('notification.paymentSuccess')
            }}
          </p>
          <p v-if="redirectTimer > 0">{{ $t('text.redirectTimer') }}</p>
        </template>
      </template>
      <template v-else>
        <p
          v-if="paymentFailed"
          ref="errorMsg"
          aria-live="assertive"
          tabindex="-1"
        >
          {{ $t('notification.paymentError') }}
        </p>
      </template>
      <template #actions v-if="paymentFailed">
        <Button @click="redirectTo">{{ $t('button.back') }}</Button>
      </template>
    </BlockContainer>
  </FlexLayout>
</template>

<script>
import {PaymentProviders, Workflow} from "@/constants/constants";
import {useAppStore} from "@/stores/app";
import Button from "@/components/actions/Button.vue";
import {paymentService} from "@/services/api/paymentService";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import VueLoading from "vue-loading-overlay";

export default {
  components: {BlockContainer, FlexLayout, Button, VueLoading},
  data() {
    return {
      paymentFailed: false,
      redirectTimer: window.config.PAYMENT.redirectTimerInSeconds, // Set the initial countdown time (in seconds)
      countdownInterval: null, // This will hold the interval ID
      isLoading: false,
    };
  },
  setup() {
    const appStore = useAppStore();

    return {
      appStore,
    };
  },
  props: {
    payment: {
      Type: String,
    },
    result: {
      Type: String,
    },
    uuid: {
      Type: String,
    },
    sessionId: {
      Type: String,
    },
    sessionResult: {
      Type: String,
    },
    hostedCheckoutId: {
      Type: String,
    },
  },
  computed: {
    isTopUp() {
      return this.appStore.isWorkflow(Workflow.TOP_UP);
    },
    isSchoolTopUp() {
      return this.appStore.isWorkflow(Workflow.SCHOOL);
    },
    isMealPlan() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN);
    }
  },
  methods: {
    startRedirectTimer() {
      this.countdownInterval = setInterval(() => {
        if (this.redirectTimer > 0) {
          this.redirectTimer--;
        } else {
          clearInterval(this.countdownInterval);
          this.redirectTo(); // Redirect when timer hits zero
        }
      }, 1000);
    },
    redirectTo() {
      if (this.isTopUp) {
        this.$navigateToCustomerCard(true);
      } else if (this.isSchoolTopUp) {
        this.$navigateToStudentAccounts(true);
      } else if (this.isMealPlan) {
        if (this.paymentFailed) {
          this.appStore.armExternalBackGuard();
          this.$navigateToMealPlanCheckout(true);
        } else {
          // First replace with shops, then push meals
          this.$navigateToMealPlanParameters(true);
          this.$nextTick(() => {
            this.$navigateToMealPlanner(false);
          });
        }
      } else {
        if (this.paymentFailed) {
          this.appStore.armExternalBackGuard();
          this.$navigateToOrderMenuCheckout(true);
        } else {
          this.$navigateToOrderMenuConfirmation();
        }
      }
    },
    async processPaymentResult() {
      if (this.payment === PaymentProviders.ADYEN) {
        const result = await paymentService.processPaymentResult(PaymentProviders.ADYEN, {
          uuid: this.uuid,
          sessionId: this.sessionId,
          sessionResult: this.sessionResult
        });
        if (result.exception || result.status !== "completed") {
          return;
        }
        this.result = "S";
      } else if (this.payment === PaymentProviders.PAYONE) {
        const result = await paymentService.processPaymentResult(PaymentProviders.PAYONE, {
          uuid: this.uuid,
          hostedCheckoutId: this.hostedCheckoutId
        });
        if (result.exception || result.status !== "PAYMENT_CREATED") {
          return;
        }
        this.result = "S";
      }
    },
    setFocusOnError() {
      this.$nextTick(() => {
        const errorMsg = this.$refs.errorMsg;
        if (errorMsg) {
          errorMsg.focus();
        }
      });
    },
  },
  async created() {
    if (this.payment === PaymentProviders.ADYEN) {
      await this.processPaymentResult();
    }
    else if (this.payment === PaymentProviders.PAYONE) {
      await this.processPaymentResult();
    }
    if (this.result === 'S') {
      if (this.isTopUp || this.isSchoolTopUp || this.isMealPlan) {
        this.startRedirectTimer(this.result);
      } else {
        this.$navigateToOrderMenuConfirmation();
      }
    } else {
      this.paymentFailed = true;
      this.setFocusOnError();
    }
  },
  beforeDestroy() {
    // Clear the interval when the component is destroyed
    clearInterval(this.countdownInterval);
  },
};
</script>
